import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { IFaehigkeit } from '../../../shared/model/faehigkeit.model';
import { ISkillCategory } from '../../../shared/model/skill-management.model';
import {
  compareText,
  hasText,
  highlightParts,
  TextPart,
} from '../skill-catalog-model';
import { SkillIconComponent } from '../skill-icon/skill-icon.component';

export interface CatalogGroup {
  key: string;
  kategorie: string;
  kategorieEn: string | null;
  skills: IFaehigkeit[];
}

@Component({
  selector: 'app-skill-catalog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslatePipe, SkillIconComponent],
  templateUrl: './skill-catalog.component.html',
  styleUrl: './skill-catalog.component.scss',
})
export class SkillCatalogComponent implements OnChanges, AfterViewInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  @Input() skills: IFaehigkeit[] = [];
  @Input() categories: ISkillCategory[] = [];
  @Input() assignmentCounts = new Map<string, number>();
  @Input() selectedId: string | null = null;
  @Output() selectSkill = new EventEmitter<string>();

  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  query = '';
  groups: CatalogGroup[] = [];
  hits: IFaehigkeit[] = [];
  hitCategoryCount = 0;
  missingEnCount = 0;
  private readonly openKeys = new Set<string>();
  private scrollToSelection = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['skills'] || changes['categories']) {
      this.groups = this.buildGroups();
      this.missingEnCount = this.skills.filter(
        (skill) => !hasText(skill.faehigkeitEn),
      ).length;
      this.updateHits();
    }
    if (changes['selectedId'] || changes['skills']) {
      this.openGroupOfSelection();
      if (this.scrollToSelection) {
        this.scrollToSelection = false;
        afterNextRender(
          () =>
            this.host.nativeElement
              .querySelector('.sk-item--on')
              ?.scrollIntoView({ block: 'nearest' }),
          { injector: this.injector },
        );
      }
    }
  }

  ngAfterViewInit(): void {
    this.searchInput?.nativeElement.focus();
  }

  /** Selects the next skill without an English name in catalog order, starting over after the last one. */
  jumpToNextMissingEnglish(): void {
    const ordered = this.groups.flatMap((group) => group.skills);
    const start = ordered.findIndex((skill) => skill.id === this.selectedId);
    const next = [
      ...ordered.slice(start + 1),
      ...ordered.slice(0, start + 1),
    ].find((skill) => !hasText(skill.faehigkeitEn));
    if (next?.id) {
      this.scrollToSelection = true;
      this.selectSkill.emit(next.id);
    }
  }

  get searching(): boolean {
    return this.query.trim().length > 0;
  }

  onQueryChange(query: string): void {
    this.query = query;
    this.updateHits();
  }

  isOpen(group: CatalogGroup): boolean {
    return this.openKeys.has(group.key);
  }

  toggle(group: CatalogGroup): void {
    if (this.openKeys.has(group.key)) {
      this.openKeys.delete(group.key);
    } else {
      this.openKeys.add(group.key);
    }
  }

  count(skill: IFaehigkeit): number {
    return this.assignmentCounts.get(skill.id ?? '') ?? 0;
  }

  parts(text: string | null | undefined): TextPart[] {
    return highlightParts(text ?? '', this.query);
  }

  showEnglishName(skill: IFaehigkeit): boolean {
    return (
      hasText(skill.faehigkeitEn) &&
      skill.faehigkeitEn!.trim() !== skill.faehigkeit?.trim()
    );
  }

  hasEnglishName(value: string | null | undefined): boolean {
    return hasText(value);
  }

  groupId(group: CatalogGroup): string {
    return 'sk-acc-' + this.groups.indexOf(group);
  }

  private buildGroups(): CatalogGroup[] {
    const groups = new Map<string, CatalogGroup>();
    for (const category of this.categories) {
      groups.set(this.keyOf(category.kategorie), {
        key: this.keyOf(category.kategorie),
        kategorie: category.kategorie,
        kategorieEn: category.kategorieEn ?? null,
        skills: [],
      });
    }
    for (const skill of this.skills) {
      const key = this.keyOf(skill.kategorie);
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          kategorie: skill.kategorie ?? '',
          kategorieEn: skill.kategorieEn ?? null,
          skills: [],
        });
      }
      groups.get(key)!.skills.push(skill);
    }
    return [...groups.values()].sort((a, b) =>
      compareText(a.kategorie, b.kategorie),
    );
  }

  private updateHits(): void {
    const needle = this.query.trim().toLowerCase();
    this.hits = needle
      ? this.skills.filter(
          (skill) =>
            (skill.faehigkeit ?? '').toLowerCase().includes(needle) ||
            (skill.faehigkeitEn ?? '').toLowerCase().includes(needle),
        )
      : [];
    this.hitCategoryCount = new Set(
      this.hits.map((skill) => this.keyOf(skill.kategorie)),
    ).size;
  }

  private openGroupOfSelection(): void {
    const selected = this.skills.find((skill) => skill.id === this.selectedId);
    if (selected) {
      this.openKeys.add(this.keyOf(selected.kategorie));
    }
  }

  private keyOf(kategorie: string | null | undefined): string {
    return (kategorie ?? '').trim().toLowerCase();
  }
}
